import omit from "lodash/omit";
import reduce from "lodash/reduce";
import { useExperienceLabelMap } from "PFApp/hooks/use_experience_label_map";
import { useImportanceLabelMap } from "PFApp/hooks/use_importance_label_map";
import { useActivitiesOptions } from "PFCore/hooks/queries/activities/use_activities_options";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { AvailabilityRule, GenericActivityEvent, Id } from "PFTypes";
import { useTranslation } from "react-i18next";

import { isStructuredAvailabilityChange, sanitizeAvailabilityRange } from "./sanitize_history_entry.utils";

type UseSanitizedAttributesReturn = {
  sanitizedAttributes: GenericActivityEvent["diff"];
  isFetching: boolean;
};

export const useSanitizedAttributes = (
  diff: GenericActivityEvent["diff"],
  metadata: GenericActivityEvent["metadata"],
  label?: string
): UseSanitizedAttributesReturn => {
  const { t } = useTranslation("activities", { keyPrefix: "show.history.entry.updatedAttributes" });
  const { t: tCore } = useTranslation("core");
  const experienceLabelMap = useExperienceLabelMap();
  const importanceLabelMap = useImportanceLabelMap();
  const { getBookingCategory } = useBookingCategories();

  const isParentActivityChange = label === "parentActivityId";
  const parentActivityIds = isParentActivityChange ? (diff as Id[]).filter(Boolean) : [];
  const { data: parentActivityOptions, isFetching } = useActivitiesOptions(
    { id: parentActivityIds },
    { enabled: parentActivityIds.length > 0 }
  );

  const sanitizedAttributes = reduce(
    diff,
    (acc, value: object, key) => {
      // Hide metadata key from the UI — flatten its contents into top level.
      // When metadata.availability uses structured format { before, after }, omit it here
      // since AvailabilityChangelog handles it separately in the parent component.
      if (key === "metadata") {
        const flatMetadata = isStructuredAvailabilityChange(metadata.availability)
          ? omit(value as object, "availability")
          : value;
        return { ...acc, ...flatMetadata! };
      }

      // Hide id key from the UI
      if (key === "id") {
        return acc;
      }

      // TODO: [SP-9670] Remove legacy history events support
      // Flatten the Availability Ranges object with Index (legacy events)
      if (key === "ranges") {
        const flattenedRanges = reduce(
          value,
          (acc, value, key) => ({
            ...acc,
            [t("range", { index: key })]: sanitizeAvailabilityRange(
              value,
              Number(key) - 1,
              metadata.availability as AvailabilityRule
            )
          }),
          {}
        );

        return { ...acc, ...flattenedRanges };
      }

      if (key === "private") {
        const privacyValues = (value as boolean[]).map((val) => t(val ? "private.true" : "private.false"));
        return { ...acc, [key]: privacyValues };
      }

      if (label === "activity_tag") {
        return [diff[0] || "-", diff[1] || "-"];
      }

      // TODO: [SP-9670] Remove legacy history events support
      // Translate the experience number values to readable labels
      if (label === "requiredExperience") {
        return [experienceLabelMap[diff[0]], experienceLabelMap[diff[1]]];
      }

      // TODO: [SP-9670] Remove legacy history events support
      // Translate the importance number values to readable labels
      if (label === "importance") {
        return [importanceLabelMap[diff[0]], importanceLabelMap[diff[1]]];
      }

      // Translate booking category ids to up-to-date category names
      if (label === "bookingCategoryId") {
        return [
          getBookingCategory(diff[0])?.display_as || diff[0],
          getBookingCategory(diff[1])?.display_as || diff[1]
        ];
      }

      // Translate parent activity ids to up-to-date activity names
      if (label === "parentActivityId") {
        return (diff as [Id | null, Id | null]).map(
          (activityId) =>
            parentActivityOptions?.entries.find((entry) => entry.id === activityId)?.name || activityId
        );
      }

      // Translate workflow state values to readable labels
      if (label === "workflowState") {
        return (diff as [string | null, string | null]).map((text) =>
          text !== null
            ? tCore(`roleStatus.workflowState.${text}` as unknown as TemplateStringsArray, {
                defaultValue: text
              })
            : null
        );
      }

      return { ...acc, [key]: value };
    },
    {}
  );

  return { sanitizedAttributes, isFetching };
};
